import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { requestLocationPermissions, isNativePlatform } from '../utils/capacitor';
import { getOrCreateMobileDeviceId } from '../utils/mobileDeviceId';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [deviceLock, setDeviceLock] = useState(null);
  const [deviceRegistration, setDeviceRegistration] = useState(null);
  const [loading, setLoading] = useState(true);

  const getStoredUser = useCallback(() => {
    try {
      const storedUser = localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch (_) {
      return null;
    }
  }, []);

  const sanitizeUserForStorage = useCallback((nextUser) => {
    if (!nextUser) {
      return nextUser;
    }

    const { profilePhotoUrl, ...storedUser } = nextUser;
    return storedUser;
  }, []);

  const mergeUserUpdates = useCallback((baseUser, updates) => {
    const nextUser = typeof updates === 'function'
      ? updates(baseUser)
      : { ...baseUser, ...updates };

    return {
      ...nextUser,
      role: nextUser?.role ?? baseUser?.role,
      email: nextUser?.email ?? baseUser?.email,
    };
  }, []);

  const persistUser = useCallback((nextUser) => {
    if (nextUser) {
      localStorage.setItem('user', JSON.stringify(sanitizeUserForStorage(nextUser)));
    }
  }, [sanitizeUserForStorage]);

  useEffect(() => {
    const initializeAuth = async () => {
      // Configure axios to send credentials (cookies) with all requests
      axios.defaults.withCredentials = true;

      // Try to verify session using cookies first (for Capacitor)
      // Cookies will be sent automatically if available
      try {
        const deviceId = await getOrCreateMobileDeviceId();
        const response = await axios.get('/api/auth/verify', {
          withCredentials: true,
          headers: deviceId ? { 'x-device-id': deviceId } : undefined
        });
        
        if (response.data.valid) {
          // Session is valid via cookie
          setUser(response.data.user);
          setDeviceLock(response.data.deviceLock || null);
          setDeviceRegistration(response.data.deviceRegistration || null);
          // Also store in localStorage as fallback
          if (response.data.token) {
            localStorage.setItem('token', response.data.token);
            persistUser(response.data.user);
            axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          }
          setLoading(false);
          return;
        }
      } catch (cookieError) {
        // Cookie-based auth failed, try localStorage fallback
        console.log('Cookie-based auth not available, trying localStorage...');
      }

      // Fallback to localStorage (for web browsers)
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');

      if (!token || !userData) {
        setLoading(false);
        return;
      }

      try {
        const deviceId = await getOrCreateMobileDeviceId();
        // Set default axios header for localStorage token
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        // Verify token with server
        const response = await axios.get('/api/auth/verify', {
          withCredentials: true,
          headers: deviceId ? { 'x-device-id': deviceId } : undefined
        });
        
        if (response.data.valid) {
          setUser(response.data.user);
          setDeviceLock(response.data.deviceLock || null);
          setDeviceRegistration(response.data.deviceRegistration || null);
        } else {
          // Token is invalid
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete axios.defaults.headers.common['Authorization'];
          setDeviceLock(null);
          setDeviceRegistration(null);
        }
      } catch (error) {
        console.error('Token verification failed:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        setDeviceLock(null);
        setDeviceRegistration(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [persistUser]);

  const login = useCallback(async (token, userData, options = {}) => {
    // Store in localStorage as fallback (for web browsers)
    localStorage.setItem('token', token);
    persistUser(userData);
    // Set Authorization header as fallback (cookies will be primary auth method)
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // Ensure credentials are sent with requests
    axios.defaults.withCredentials = true;
    setUser(userData);
    setDeviceLock(options.deviceLock || null);
    setDeviceRegistration(options.deviceRegistration || null);

    // Request location permissions after successful login (mobile only)
    // Use a more defensive approach to prevent crashes
    if (isNativePlatform()) {
      // Wait longer for the UI to fully settle before requesting permissions
      // This prevents crashes that can occur if the app tries to request permissions
      // before the native bridge is fully initialized
      setTimeout(async () => {
        try {
          // Double-check that we're still on native platform
          if (!isNativePlatform()) {
            return;
          }
          
          // Check if Geolocation is available before requesting
          // This prevents crashes if the plugin isn't properly initialized
          const { checkLocationPermissions } = await import('../utils/capacitor');
          const currentStatus = await checkLocationPermissions();
          
          // Only request if permission is not already granted
          if (currentStatus.location !== 'granted') {
            await requestLocationPermissions();
          }
        } catch (error) {
          // Silently handle errors - don't crash the app
          // Log for debugging but don't show to user
          console.warn('Location permission request after login failed (non-critical):', error.message);
          // Don't block login if permission request fails
        }
      }, 2000); // Increased delay to 2 seconds for better stability
    }
  }, [persistUser]);

  const updateUser = useCallback((updates) => {
    if (!updates) {
      return null;
    }

    let resolvedUser = null;
    setUser((currentUser) => {
      const baseUser = currentUser || getStoredUser() || {};
      resolvedUser = mergeUserUpdates(baseUser, updates);
      persistUser(resolvedUser);
      return resolvedUser;
    });
    return resolvedUser;
  }, [getStoredUser, mergeUserUpdates, persistUser]);

  const logout = useCallback(async () => {
    try {
      // Call server logout endpoint to clear cookies
      await axios.post('/api/auth/logout', {}, {
        withCredentials: true
      });
    } catch (error) {
      // Continue with logout even if server call fails
      console.warn('Logout API call failed, continuing with local logout:', error);
    }
    
    // Clear localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
    setDeviceLock(null);
    setDeviceRegistration(null);
    // Force redirect to login page
    window.location.href = '/login';
  }, []);

  const isAdmin = useCallback(() => {
    return user?.role === 'admin' || user?.role === 'manager-admin';
  }, [user]);

  const isUser = useCallback(() => {
    return user?.role === 'user';
  }, [user]);

  const isManager = useCallback(() => {
    // A user is a manager if they have manager privileges but are not in manager-admin role
    return user?.role === 'user' && (user?.isManager || user?.isTeamLead || user?.hasSubordinates);
  }, [user]);

  const isSuperAdmin = useCallback(() => {
    return (user?.role === 'admin' || user?.role === 'manager-admin') && user?.isSuperAdmin;
  }, [user]);

  const isManagerAdmin = useCallback(() => {
    // Check if user has manager privileges (can access admin panel)
    return user?.role === 'user' && (user?.isManager || user?.isTeamLead || user?.hasSubordinates);
  }, [user]);

  const value = useMemo(() => ({
    user,
    deviceLock,
    setDeviceLock,
    deviceRegistration,
    setDeviceRegistration,
    loading,
    login,
    logout,
    updateUser,
    isAdmin,
    isUser,
    isManager,
    isSuperAdmin,
    isManagerAdmin,
    isAuthenticated: !!user
  }), [
    user,
    deviceLock,
    deviceRegistration,
    loading,
    login,
    logout,
    updateUser,
    isAdmin,
    isUser,
    isManager,
    isSuperAdmin,
    isManagerAdmin
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
